<script setup lang="ts">
import { ref } from 'vue';
import { z } from 'zod';

import { createLocalStore } from '@/core/local-store';

const props = withDefaults(
  defineProps<{
    // Unique per embed - used as the local storage key, so consenting to one
    // third-party embed doesn't implicitly consent to a different one.
    id: string;
    // Display name of the third-party service, shown in the consent prompt.
    name: string;
    // Optional preview shown behind the consent prompt, e.g. a static
    // screenshot standing in for the live embed until it's allowed to load.
    image?: string;
    position?: 'center' | 'top' | 'bottom';
  }>(),
  { image: undefined, position: 'center' },
);

const embedConsentStore = createLocalStore(
  `embedConsent:${props.id}`,
  z.object({ given: z.literal(true) }),
);

const consentGiven = ref(!!embedConsentStore.read());

function onShow() {
  consentGiven.value = true;
  embedConsentStore.write({ given: true });
}
</script>

<template>
  <slot v-if="consentGiven" />

  <div
    v-else
    :class="$style.prompt"
    :style="image ? { backgroundImage: `url(${image})` } : undefined"
  >
    <div :class="[$style.card, $style[position]]">
      <p class="mb-0">
        {{ $t`Enabling this content shares your data with ${name}.` }}
      </p>
      <button
        type="button"
        class="btn btn-dark btn-sm"
        :class="$style.button"
        @click="onShow"
      >
        {{ $t`Enable` }}
      </button>
    </div>
  </div>
</template>

<style lang="scss" module>
.prompt {
  position: relative;
  height: 100%;
  min-height: 12rem;
  background-color: #e6e6e6;
  background-size: cover;
  background-position: center;
}

.card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: rgb(255 255 255 / 85%);
}

.center {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  flex-direction: column;
  max-width: 20rem;
  padding: 1rem 1.5rem;
  border-radius: 0.25rem;
  text-align: center;
}

.top,
.bottom {
  position: absolute;
  left: 0;
  right: 0;
  justify-content: space-between;
  padding: 0.5rem 1rem;

  .button {
    flex-shrink: 0;
  }
}

.top {
  top: 0;
}

.bottom {
  bottom: 0;
}
</style>
